import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/feature/SEOHead";
import { getArticleById, getRelatedArticles } from "@/mocks/articles";

export default function ArticleDetailPage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [id]);

  const article = getArticleById(id || "");

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 md:pt-24 min-h-[60vh] flex items-center justify-center bg-background-50">
          <div className="text-center px-4">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-3">
              文章未找到
            </h1>
            <p className="text-foreground-600 mb-6">抱歉，你访问的文章不存在或已被移除。</p>
            <Link
              to="/articles"
              className="inline-flex items-center gap-1.5 px-6 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line text-sm"></i>
              返回文章列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article.id, article.category, 3);
  const canonical = `https://ribenhanou-style.com/article/${article.id}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: article.title,
        description: article.excerpt,
        image: article.image,
        datePublished: article.date,
        author: { "@type": "Person", name: article.author },
        publisher: {
          "@type": "Organization",
          name: "日韩欧美 Style Hub",
        },
        mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://ribenhanou-style.com/" },
          { "@type": "ListItem", position: 2, name: "潮流文章", item: "https://ribenhanou-style.com/articles" },
          { "@type": "ListItem", position: 3, name: article.title, item: canonical },
        ],
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - 日韩欧美潮流`}
        description={article.excerpt}
        keywords={article.tags.join(",")}
        canonicalUrl={canonical}
        ogType="article"
        ogImage={article.image}
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <div className="w-full px-4 md:px-6 py-6 bg-background-100">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "潮流文章", to: "/articles" },
              { label: article.title },
            ]}
          />
        </div>

        <article className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-2.5 py-1 text-xs font-medium bg-accent-500/90 text-background-50 rounded-full">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-500">{article.date}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                <span className="text-xs text-foreground-500">{article.readTime}阅读</span>
                <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                <span className="text-xs text-foreground-500">{article.author}</span>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {article.excerpt}
              </p>
            </div>

            <div className="mb-8 rounded-lg overflow-hidden aspect-[16/9]">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed">
              {article.content.split("\n\n").map((paragraph, idx) => {
                if (paragraph.startsWith("## ")) {
                  return (
                    <h2 key={idx} className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-8 mb-4">
                      {paragraph.replace("## ", "")}
                    </h2>
                  );
                }
                return (
                  <p key={idx} className="mb-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                    {paragraph}
                  </p>
                );
              })}
            </div>

            <div className="mt-8 pt-6 border-t border-background-200">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-foreground-500">标签:</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs bg-secondary-100 text-secondary-800 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative rounded-lg overflow-hidden aspect-[16/10] mb-3">
                      <img
                        src={rel.image}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <h3 className="text-base font-heading font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2">
                      {rel.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">{rel.date}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}